import { Users } from "lucide-react";

/**
 * "Who SAINT® is for" — the types of patients we care for.
 *
 * A warm, informational section that frames /saint around the people it
 * serves rather than as a competitor comparison. Every description speaks to
 * a real patient profile we see at the clinic. No competitor names, no
 * price callouts, and no side-by-side tables live here — those belong on
 * the dedicated comparison and cost pages.
 */
const PATIENT_TYPES = [
  {
    title: "Young adults",
    emoji: "🎓",
    body: "College students and early-career adults whose depression began or intensified in their twenties and thirties — people who want a non-medication option that won't put life on hold for months.",
  },
  {
    title: "Parents & caregivers",
    emoji: "👨‍👩‍👧",
    body: "Mothers and fathers carrying the weight of a family while depression drains their energy. A five-day course means a defined, short time away — not six weeks of appointments.",
  },
  {
    title: "Older adults",
    emoji: "🕊️",
    body: "Adults in their fifties, sixties, and seventies who have lived with depression for years and want a non-invasive, medication-free alternative that fits later-in-life health considerations.",
  },
  {
    title: "Executives & professionals",
    emoji: "💼",
    body: "High-performers who can't step away for six to nine weeks of sessions. They need the clearest possible time commitment and private scheduling so they can return to work quickly.",
  },
  {
    title: "Professional athletes",
    emoji: "🏅",
    body: "Competitive athletes managing depression without the performance and weight effects of medication. A short, defined treatment window keeps training and competition timelines intact.",
  },
];

export const SaintWhoItsFor = () => (
  <section className="border-b border-border bg-muted/20 py-14 sm:py-20">
    <div className="container-px mx-auto max-w-6xl px-4">
      <div className="mb-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-accent">
          <Users className="h-4 w-4" />
          Who SAINT® is for
        </span>
        <h2 className="mt-4 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          The patients we care for
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
          SAINT® is for adults with major depressive disorder who haven't found
          enough relief from antidepressants. The people who come to us share
          one thing: they want their life back, and they don't want to wait six
          weeks to start living it again.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PATIENT_TYPES.map((p) => (
          <div
            key={p.title}
            className="rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:shadow-premium"
          >
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-2xl">
              <span aria-hidden="true">{p.emoji}</span>
            </div>
            <h3 className="font-headings text-lg font-bold text-primary">
              {p.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/80">
              {p.body}
            </p>
          </div>
        ))}

        {/* CTA card — fills the sixth grid cell on lg */}
        <div className="flex flex-col justify-center rounded-2xl border-2 border-accent/30 bg-accent/[0.04] p-6 text-center shadow-soft">
          <h3 className="font-headings text-lg font-bold text-primary">
            Not sure where you fit?
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/80">
            Eligibility is based on your diagnosis and history, not a
            demographic. Take the two-minute check and a clinician will confirm
            whether SAINT® is right for your situation.
          </p>
          <a
            href="#saint-articles"
            className="mt-4 inline-flex items-center justify-center rounded-xl bg-gradient-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-glow transition hover:opacity-90"
          >
            See if SAINT® fits
          </a>
        </div>
      </div>

      <p className="mx-auto mt-8 max-w-2xl text-center text-xs text-muted-foreground">
        Individual results vary. No outcome is guaranteed. Final candidacy is
        confirmed by our medical team during your consultation.
      </p>
    </div>
  </section>
);

export default SaintWhoItsFor;
